import React, { useState, useRef, useCallback } from 'react';
import { View, Text, StyleSheet, TextInput, TouchableOpacity, ScrollView, Pressable } from 'react-native';
import { ChevronDown, Search, X } from 'lucide-react';
import { webColors, webBorderRadius } from '../../constants/webStyles';
import { WORLD_COUNTRIES_ARRAY, searchCountries, getWorldCountryData } from '../../constants/worldCountries';

const WebCountrySelect = ({ value, onChange, placeholder = 'Select Country', error, label }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPos, setDropdownPos] = useState(null);
  const buttonRef = useRef(null);

  const openDropdown = useCallback(() => {
    if (buttonRef.current) {
      buttonRef.current.measureInWindow((x, y, width, height) => {
        setDropdownPos({ top: y + height + 4, left: x, width });
        setIsOpen(true);
      });
    } else {
      setIsOpen(true);
    }
  }, []);

  const filteredCountries = searchTerm
    ? searchCountries(searchTerm)
    : WORLD_COUNTRIES_ARRAY;

  const selectedCountry = value ? getWorldCountryData(value) : null;

  const handleSelect = (countryCode) => {
    onChange(countryCode);
    setIsOpen(false);
    setSearchTerm('');
  };

  return (
    <View style={styles.container}>
      {label && <Text style={styles.label}>{label}</Text>}
      <TouchableOpacity
        ref={buttonRef}
        style={[styles.selectButton, error && styles.selectButtonError, isOpen && styles.selectButtonActive]}
        onPress={() => isOpen ? setIsOpen(false) : openDropdown()}
        activeOpacity={0.7}
      >
        <Text style={[styles.selectButtonText, !selectedCountry && styles.selectButtonPlaceholder]}>
          {selectedCountry ? `${selectedCountry.flag} ${selectedCountry.name}` : placeholder}
        </Text>
        <ChevronDown size={16} color={webColors.textMuted} />
      </TouchableOpacity>
      {error && <Text style={styles.errorText}>Please select a country</Text>}

      {isOpen && (
        <>
          <Pressable
            style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9998 }}
            onPress={() => { setIsOpen(false); setSearchTerm(''); }}
          />
          <View style={[styles.dropdown, dropdownPos && {
            position: 'fixed',
            top: dropdownPos.top,
            left: dropdownPos.left,
            width: dropdownPos.width,
            right: undefined,
            zIndex: 9999,
          }]}>
            <View style={styles.searchWrapper}>
              <Search size={14} color={webColors.textMuted} />
              <TextInput
                style={styles.searchInput}
                placeholder="Search countries..."
                placeholderTextColor={webColors.textMuted}
                value={searchTerm}
                onChangeText={setSearchTerm}
                autoFocus
              />
              {searchTerm ? (
                <TouchableOpacity onPress={() => setSearchTerm('')}>
                  <X size={14} color={webColors.textMuted} />
                </TouchableOpacity>
              ) : null}
            </View>
            <ScrollView style={styles.optionsList} nestedScrollEnabled>
              {filteredCountries.map((country) => (
                <TouchableOpacity
                  key={country.code}
                  style={[styles.option, value === country.code && styles.optionSelected]}
                  onPress={() => handleSelect(country.code)}
                >
                  <Text style={styles.optionFlag}>{country.flag}</Text>
                  <Text style={[styles.optionName, value === country.code && styles.optionNameSelected]}>
                    {country.name}
                  </Text>
                </TouchableOpacity>
              ))}
              {filteredCountries.length === 0 && (
                <Text style={styles.noResults}>No countries found</Text>
              )}
            </ScrollView>
          </View>
        </>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: { position: 'relative', zIndex: 10 },
  label: { fontSize: 15, fontWeight: '700', color: webColors.textPrimary, marginBottom: 6 },
  selectButton: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    backgroundColor: webColors.backgroundAlt, borderWidth: 1, borderColor: webColors.border,
    borderRadius: webBorderRadius.medium, padding: 14, paddingHorizontal: 16,
  },
  selectButtonActive: { borderColor: webColors.primary },
  selectButtonError: { borderColor: webColors.error },
  selectButtonText: { fontSize: 14, color: webColors.textPrimary },
  selectButtonPlaceholder: { color: webColors.textMuted },
  errorText: { fontSize: 12, color: webColors.error, marginTop: 4 },
  dropdown: {
    position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4,
    backgroundColor: webColors.cardBackground, borderWidth: 1, borderColor: webColors.border,
    borderRadius: webBorderRadius.medium,
    shadowColor: '#000', shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.1, shadowRadius: 12,
    elevation: 5, zIndex: 100,
  },
  searchWrapper: {
    flexDirection: 'row', alignItems: 'center', padding: 12,
    borderBottomWidth: 1, borderBottomColor: webColors.borderLight, gap: 8,
  },
  searchInput: { flex: 1, fontSize: 14, color: webColors.textPrimary, padding: 0 },
  optionsList: { maxHeight: 200 },
  option: { flexDirection: 'row', alignItems: 'center', padding: 12, paddingHorizontal: 16, gap: 10 },
  optionSelected: { backgroundColor: webColors.primaryLight },
  optionFlag: { fontSize: 18 },
  optionName: { fontSize: 14, color: webColors.textPrimary },
  optionNameSelected: { color: webColors.primary, fontWeight: '600' },
  noResults: { padding: 16, textAlign: 'center', color: webColors.textMuted, fontSize: 13 },
});

export default WebCountrySelect;
